/* eslint-disable react/no-array-index-key */

import { type ForwardedRef, forwardRef, type HTMLAttributes } from 'react';
import styled from 'styled-components';
import type { GetScalingAttrProps } from 'helpers/imageScalingHelper';
import Image from '../Image';
import type { AspectRatioValue } from '../Image/shared/types';
import PictureSource from './PictureSource';
import type { PictureProps, PictureSourceProps } from './shared/types';

const ImageWithScaling = styled(Image.WithScaling)`
    max-width: 100%;
    height: auto;
    width: auto;
`;

const AspectRatioContainer = styled.div<{ $aspectRatio: AspectRatioValue }>`
    display: flex;
    max-width: 100%;
    max-height: 100%;

    img {
        object-fit: cover;
        aspect-ratio: ${({ $aspectRatio }) => $aspectRatio};
        overflow: hidden;
    }
`;

function getHighestBreakpoint(data): PictureSourceProps {
    return data.reduce((prev, curr) => (prev.breakpoint > curr.breakpoint ? prev : curr));
}

export function mapSizesFromSources(sources: PictureSourceProps[]): GetScalingAttrProps['sizes'] {
    const removedDuplicates = new Set(sources.flatMap((source) => source.sizes));

    return Array.from(removedDuplicates);
}

const PictureContent = forwardRef(
    (props: PictureProps & HTMLAttributes<HTMLPictureElement>, ref: ForwardedRef<HTMLPictureElement>): JSX.Element => {
        const {
            alt,
            sources,
            transformType = 'WidthScale',
            imageType = 'default',
            fallbackSrc,
            aspectRatio,
            loading,
            ...restProps
        } = props;
        const fallbackUrl = fallbackSrc || getHighestBreakpoint(sources).src;
        const mappedSizes = mapSizesFromSources(sources);

        return (
            <picture ref={ref} {...restProps}>
                {sources.map((source, index) => (
                    <PictureSource key={index} source={source} transformType={transformType} imageType={imageType} />
                ))}
                <ImageWithScaling
                    alt={alt}
                    transformType={transformType}
                    imageType={imageType}
                    src={fallbackUrl}
                    sizes={mappedSizes}
                    loading={loading}
                />
            </picture>
        );
    }
);

const Picture = forwardRef(
    (props: PictureProps & HTMLAttributes<HTMLPictureElement>, ref: ForwardedRef<HTMLPictureElement>): JSX.Element => {
        const { aspectRatio } = props;

        if (aspectRatio) {
            return (
                <AspectRatioContainer $aspectRatio={aspectRatio}>
                    <PictureContent ref={ref} {...props} />
                </AspectRatioContainer>
            );
        }

        return <PictureContent ref={ref} {...props} />;
    }
);

export default Picture;
